import type { InspectedDoc } from "@/components/layout/TreeViewOutletContext";
import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SharePanel } from "@/features/clipboard/components/SharePanel";
import { importFromGoogleDrive, isDriveConfigured } from "@/features/clipboard/googleDrive";
import { UsageAlert } from "@/features/usage/components/UsageAlert";
import { useWorkspaceStorage } from "@/features/usage/hooks/useLimit";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/** A quiet contents-page row, matching the primary nav in WorkspaceSidebar. */
const LINK_ROW =
  "font-ui block w-full text-left text-zinc-400 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:text-zinc-900 disabled:pointer-events-none disabled:text-zinc-300";

/** Top-level verbs (Add files, Chat about these…) sit a step darker than the
 * detail links so they read as actions rather than metadata. */
const ACTION_ROW =
  "font-ui block w-full text-left text-zinc-600 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:text-zinc-900 disabled:pointer-events-none disabled:text-zinc-300";

const formatBytes = (bytes: number | null): string => {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const asString = (v: unknown): string => (typeof v === "string" ? v : "");
const asNumber = (v: unknown): number | null => (typeof v === "number" ? v : null);

const linkedPaperSource = (doc: NonNullable<InspectedDoc>): string => {
  const meta =
    doc.corpus_meta && typeof doc.corpus_meta === "object"
      ? (doc.corpus_meta as Record<string, unknown>)
      : {};
  const configured = [meta.source, meta.server, meta.corpus, meta.archive].find(
    (value) => typeof value === "string" && value.trim(),
  );
  if (typeof configured === "string") {
    const normalized = configured.toLowerCase();
    if (normalized.includes("arxiv")) return "arXiv";
    if (normalized.includes("biorxiv")) return "bioRxiv";
    if (normalized.includes("medrxiv")) return "medRxiv";
    if (normalized.includes("pmc") || normalized.includes("pubmed")) {
      return "PubMed Central";
    }
    if (normalized.includes("fda")) return "FDA";
    if (normalized.includes("trial")) return "ClinicalTrials.gov";
    return configured;
  }

  const id = asString(doc.corpus_doc_id).toLowerCase();
  if (id.startsWith("arx_") || id.includes("arxiv")) return "arXiv";
  if (id.startsWith("bio_") || id.includes("biorxiv")) return "bioRxiv";
  if (id.startsWith("med_") || id.includes("medrxiv")) return "medRxiv";
  if (id.startsWith("pmc")) return "PubMed Central";
  if (id.startsWith("fda")) return "FDA";
  if (id.startsWith("nct")) return "ClinicalTrials.gov";
  return "Paperclip";
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      aria-label="Copy location"
      title="Copy location"
      className="mt-1 shrink-0 text-zinc-400 transition-colors hover:text-zinc-700"
    >
      {copied ? <Check className="h-3 w-3 text-zinc-900" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
    </button>
  );
}


/** Metadata + processing detail for the row currently inspected in the list. */
function DetailsPanel({
  doc,
  onShareFolder,
  onShareDocument,
  onView,
  onViewOriginal,
  onDownload,
}: {
  doc: NonNullable<InspectedDoc>;
  onShareFolder: (folder: string) => void;
  onShareDocument: (docId: string, name: string) => void;
  onView: () => void;
  onViewOriginal: () => void;
  onDownload: () => void;
}) {
  const filename = asString(doc.filename);
  const folderPath = asString(doc.folder_path);
  const isLink = asString(doc.link_type) === "corpus_link" || doc.source_type === "link";
  const ext = filename.includes(".") ? filename.split(".").pop()?.toUpperCase() : "";
  const typeLabel = isLink
    ? "Corpus link"
    : doc.source_type === "bib"
      ? "Bibliography"
      : ext || "File";
  const source = isLink
    ? linkedPaperSource(doc)
    : doc.source_type === "bib"
      ? "Bibliography"
      : "Uploaded by you";
  const status = doc.index_status;
  const contentStatus = doc.content_status;
  const statusText = isLink
    ? "Read-only"
    : contentStatus === "queued" || contentStatus === "quick_ready"
      ? "Added — waiting to process"
      : contentStatus === "processing"
        ? "Extracting rich content"
        : contentStatus === "partial"
          ? "Ready with quick text"
          : contentStatus === "failed"
            ? "Content processing failed"
            : status == null || status === "ready"
              ? "Fully indexed"
              : status === "dead"
                ? "Indexing failed"
                : "Indexing…";

  const leaf = `usr_${asString(doc.id).replace(/-/g, "").slice(0, 12)}`;
  const location = folderPath ? `/clipboard/${folderPath}/${leaf}` : `/clipboard/${leaf}`;

  // Durable pipeline: the paper appears after quick extraction; richer
  // processing and search indexing advance independently in the background.
  const indexed = status == null || status === "ready";
  const indexing = status === "pending" || status === "processing" || status === "awaiting_refresh" || status === "retry";
  const enhanced =
    contentStatus == null ||
    contentStatus === "enhanced" ||
    contentStatus === "partial";
  const enhancing =
    contentStatus === "queued" ||
    contentStatus === "quick_ready" ||
    contentStatus === "processing";
  const steps = isLink
    ? []
    : [
      { label: "Added", state: "done" as const },
      ...(contentStatus === "queued"
        ? []
        : [{ label: "Quick text ready", state: "done" as const }]),
      {
        label:
          contentStatus === "partial"
            ? "Rich extraction incomplete"
            : "Rich content extracted",
        state: (enhanced ? "done" : enhancing ? "active" : "idle") as
          | "done"
          | "active"
          | "idle",
      },
      {
        label: "Search index ready",
        state: (indexed ? "done" : indexing ? "active" : "idle") as "done" | "active" | "idle",
      },
    ];

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="font-ui shrink-0 text-zinc-400">{label}</dt>
      <dd className="min-w-0 truncate text-right text-zinc-700">{children}</dd>
    </div>
  );

  return (
    <div className="space-y-5 px-5 text-sm leading-6">
      <div>
        <p className="font-ui text-zinc-400">Selected</p>
        <p className={`mt-1 text-base leading-6 text-zinc-900 [overflow-wrap:anywhere] line-clamp-3 ${isLink ? "italic" : ""}`}>
          {filename}
        </p>
      </div>

      {/* Ways to open it, set as a short contents list */}
      <ul className="space-y-0.5">
        <li>
          <button type="button" onClick={onView} className={LINK_ROW}>
            Read
          </button>
        </li>
        {!isLink && (
          <li>
            <button type="button" onClick={onDownload} className={LINK_ROW}>
              Download
            </button>
          </li>
        )}
        {!isLink && doc.source_type !== "folder" && (
          <li>
            <button
              type="button"
              onClick={onViewOriginal}
              title="Open the file you uploaded (rendered PDF/HTML), not the parsed version"
              className={LINK_ROW}
            >
              Open original{ext ? ` (.${ext.toLowerCase()})` : ""}
            </button>
          </li>
        )}
      </ul>

      <dl className="space-y-0.5 border-t border-zinc-200/70 pt-3">
        <Row label="Type">{typeLabel}</Row>
        <Row label="Source">{source}</Row>
        <Row label="Status">{statusText}</Row>
        {!isLink && (
          <Row label="Size">
            {formatBytes(asNumber(doc.file_size_bytes))}
            {asNumber(doc.page_count) ? ` · ${asNumber(doc.page_count)} pp` : ""}
          </Row>
        )}
        <div className="pt-1">
          <dt className="font-ui text-zinc-400">Location</dt>
          <dd className="mt-0.5 flex items-start gap-2">
            <code className="min-w-0 flex-1 [overflow-wrap:anywhere] font-mono text-2xs leading-5 text-zinc-500">
              {location}
            </code>
            <CopyButton value={location} />
          </dd>
        </div>
      </dl>

      {steps.length > 0 && (
        <div className="border-t border-zinc-200/70 pt-3">
          <p className="font-ui text-zinc-400">Processing</p>
          <ol className="mt-1 space-y-0.5">
            {steps.map((s) => (
              <li
                key={s.label}
                className={`flex items-baseline gap-2 ${s.state === "done"
                  ? "text-zinc-700"
                  : s.state === "active"
                    ? "italic text-zinc-600"
                    : "text-zinc-300"
                  }`}
              >
                <span aria-hidden className="w-3 shrink-0 text-center">
                  {s.state === "done" ? "✓" : s.state === "active" ? "…" : "·"}
                </span>
                {s.label}
              </li>
            ))}
          </ol>
          {(contentStatus === "failed" || contentStatus === "partial") && (
            <p className="mt-2 text-sm leading-5 text-amber-800">
              {contentStatus === "partial"
                ? "Quick text remains available, but rich extraction could not complete."
                : "Rich extraction could not complete. Delete and add the file again to retry."}
            </p>
          )}
        </div>
      )}

      {!isLink && (
        <ul className="space-y-0.5 border-t border-zinc-200/70 pt-3">
          <li>
            <button
              type="button"
              onClick={() => onShareDocument(asString(doc.id), filename)}
              className={LINK_ROW}
            >
              Share this file
            </button>
          </li>
          {folderPath && (
            <li>
              <button type="button" onClick={() => onShareFolder(folderPath)} className={LINK_ROW}>
                Share the “{folderPath}” folder
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * The Clipboard-specific sidebar content: quick "Add to Clipboard" / "Start
 * chat with selection" actions plus the Details panel for the inspected row.
 * Rendered inside the unified WorkspaceSidebar's Clipboard accordion section
 * (and, historically, by TreeViewSidebar). Reads clipboard state from
 * TreeViewOutletContext, which is null outside the clipboard/repos shell.
 */
export const ClipboardSidebarSections = () => {
  const { clipboardActions, inspectedDoc } = useTreeViewOutletContext();
  const [sharingFolder, setSharingFolder] = useState<string | null>(null);
  const [sharingDoc, setSharingDoc] = useState<{ id: string; name: string } | null>(null);
  const [driveBusy, setDriveBusy] = useState(false);
  const {
    isAtLimit: storageAtLimit,
    isApproachingLimit: storageApproaching,
    limit: storageLimit,
  } = useWorkspaceStorage();

  const handleDriveImport = async () => {
    if (driveBusy || storageAtLimit || !clipboardActions) return;
    setDriveBusy(true);
    try {
      const files = await importFromGoogleDrive();
      if (files.length === 0) {
        toast.info("No files selected from Google Drive.");
        return;
      }
      clipboardActions.onImportFiles(files);
      toast.success(`Importing ${files.length} file${files.length === 1 ? "" : "s"} from Google Drive.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Google Drive import failed.");
    } finally {
      setDriveBusy(false);
    }
  };

  const canModify = clipboardActions?.canModify ?? false;

  const selectionCount = clipboardActions?.selectionCount ?? 0;

  return (
    <>
      <nav
        className="flex min-h-0 flex-1 flex-col gap-5 pb-4"
        aria-label="Clipboard actions"
      >
        {/* Actions read as plain verbs in one contents list — no section
            captions. Selection-dependent verbs only appear once something is
            selected, carrying the count in the label. */}
        {clipboardActions && canModify && (
          <div className="px-5">
            <UsageAlert
              id="usage-alert-clipboard-storage"
              name="workspaceStorage"
              isAtLimit={storageAtLimit}
              isApproachingLimit={storageApproaching}
              limit={storageLimit}
            />
          </div>
        )}
        {clipboardActions && (canModify || selectionCount > 0) && (
          <ul className="space-y-0.5 px-5 text-sm leading-6">
            {canModify && (
              <li>
                {/* One "+ New" control carrying the add actions, like a drive. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-describedby={storageAtLimit || storageApproaching ? "usage-alert-clipboard-storage" : undefined}
                      className="font-ui inline-flex items-center gap-2 rounded-[3px] border border-zinc-900 bg-white px-3 py-1 leading-6 text-zinc-900 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900 data-[state=open]:bg-zinc-50"
                    >
                      <span aria-hidden className="text-base leading-none pb-0.5">+</span>
                      New
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[12rem]">
                    <DropdownMenuItem
                      disabled={storageAtLimit}
                      onSelect={() => clipboardActions.onUploadFiles()}
                    >
                      Upload files
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={storageAtLimit}
                      onSelect={() => clipboardActions.onUploadFolder()}
                    >
                      Upload a folder
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => clipboardActions.onCreateFolder()}>
                      New folder
                    </DropdownMenuItem>
                    {isDriveConfigured() && (
                      <DropdownMenuItem
                        disabled={driveBusy || storageAtLimit}
                        onSelect={() => void handleDriveImport()}
                      >
                        {driveBusy ? "Importing from Google Drive…" : "Import from Google Drive"}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            )}
            {selectionCount > 0 && (
              <>
                <li className={canModify ? "pt-3" : undefined}>
                  <button type="button" onClick={() => clipboardActions.onStartChat()} className={ACTION_ROW}>
                    Chat about {selectionCount === 1 ? "this" : `these ${selectionCount}`}
                  </button>
                </li>
                {canModify && clipboardActions.selectedDocCount > 0 && (
                  <li>
                    <button
                      type="button"
                      onClick={() => clipboardActions.onBulkDelete()}
                      className={`${ACTION_ROW} hover:text-red-700`}
                    >
                      Delete{" "}
                      {clipboardActions.selectedDocCount === 1
                        ? "this file"
                        : `${clipboardActions.selectedDocCount} files`}
                    </button>
                  </li>
                )}
              </>
            )}
          </ul>
        )}

        <div className="min-h-0 flex-1 pt-4">
          {inspectedDoc ? (
            <DetailsPanel
              doc={inspectedDoc}
              onShareFolder={setSharingFolder}
              onShareDocument={(id, name) => setSharingDoc({ id, name })}
              onView={() => clipboardActions?.onView(inspectedDoc)}
              onViewOriginal={() => clipboardActions?.onViewOriginal(inspectedDoc)}
              onDownload={() => clipboardActions?.onDownload(inspectedDoc)}
            />
          ) : (
            <p className="font-serif px-5 text-sm leading-6 text-zinc-400">
              Select a file to see its details, or select several to chat about them
              {canModify ? " or delete them" : ""}.
            </p>
          )}
        </div>
      </nav>

      {sharingFolder && (
        <SharePanel folderPath={sharingFolder} onClose={() => setSharingFolder(null)} />
      )}
      {sharingDoc && (
        <SharePanel
          documentId={sharingDoc.id}
          documentName={sharingDoc.name}
          onClose={() => setSharingDoc(null)}
        />
      )}
    </>
  );
};
